// Native-storage mirror of the AMS session (see jwt.ts's header comment for the
// other half of this pair) — lets a wrapped app restore login without a cookie
// round trip. Takes origin as a plain parameter rather than resolving it itself,
// since ams-functions.ts will import this module and importing back would be
// circular.
import { isAndroidORIosApp } from 'webtonative'
import { deleteAppData, getAppData, setAppData } from 'webtonative/NativeDatastore'
import { decodeJwtExpiry } from './jwt'
import { noteNativeStoreEvent } from './sessionTelemetry'

// Single fixed key: this module only ever mirrors "the current session", never
// a collection of them.
const STORE_KEY = 'icc_session'

// The bridge callback is simply never invoked outside a wrapper (or on a wrapper
// too old for this API) — a timeout is the only way to notice that instead of
// hanging forever.
const NATIVE_CALL_TIMEOUT_MS = 1500

// Device/server clock drift can make a still-valid token's exp read as already
// past; small enough to not meaningfully extend a genuinely expired session.
const CLOCK_SKEW_SLACK_MS = 30 * 1000

/** Versioned so a future change to this shape can tell old stored payloads apart instead of guessing. */
export type NativeSessionPayload = {
    v: 1
    jwt: string
    userId: string
    origin: string
    savedAt: number
    // Telemetry seed, carried so a restored session keeps accruing lifetime
    // instead of looking brand new on every cold start. Optional on purpose:
    // payloads written before this existed must still validate and restore.
    sessionId?: string
    startedAt?: number
}

/** Tri-state, not boolean, so "never checked" is distinguishable from "checked and it's missing/broken". */
export type StoreAvailability = 'unknown' | 'available' | 'unavailable'

let cachedAvailability: StoreAvailability = 'unknown'

// The underlying bridge (webtonative/utills.js's registerCb) has no per-call
// correlation id: a response with no reqType fires and deletes *every* pending
// callback, matching only by NativeDatastore's own `type` filter. Two calls to
// this module in flight at once can therefore cross-deliver a response to the
// wrong caller or silently orphan one of them (never firing again). Serializing
// every call through this queue guarantees at most one bridge call is pending
// from this module at a time, which also makes `cachedAvailability` writes
// strictly ordered — no timeout from an earlier call can ever clobber a later
// call's fresher result, since the earlier call has already settled by the time
// a later one starts.
let callQueue: Promise<unknown> = Promise.resolve()

// Last successfully-enqueued session write this page load. Re-writing an
// unchanged session isn't just wasteful: every bridge call risks the SDK's
// shared-registry hazard above, and the commonest trigger is the resume-time
// session check firing right when a native sheet (SSO, IAP) closes — exactly
// when another feature's callback is pending. Skipping no-op writes removes
// that traffic; a renew (new jwt) or identity change still writes.
let lastWrittenSession: string | null = null

function enqueue<T>(task: () => Promise<T>): Promise<T> {
    const result = callQueue.then(task, task)
    callQueue = result.then(
        () => undefined,
        () => undefined,
    )
    return result
}

// Vendor shapes, kept local instead of imported: the package's `exports` map
// only publishes the `webtonative/NativeDatastore` subpath, not the `/type`
// module it's typed against, so importing the latter type-checks under this
// project's `moduleResolution: node` but breaks the Vite build (see the
// Facebook/events precedent in src/wtnGlobal.ts).
type NativeAppDataResponse = { isSuccess: boolean; value?: any; keyName?: string }
type NativeDeleteResponse = { isSuccess: boolean }

/** Read-only outward view of the cache — nothing outside this module may set it directly. */
export const getStoreAvailability = (): StoreAvailability => cachedAvailability

// Races a bridge call against NATIVE_CALL_TIMEOUT_MS. `settled` is captured
// fresh per invocation, so a real callback that arrives after its own call's
// timeout already fired is a guaranteed no-op — it can't resolve twice, and it
// can't affect any other in-flight or later call.
//
// `onUnavailable` fires for BOTH ways a bridge can be absent, which differ by
// platform: an old iOS shell accepts the postMessage and simply never answers
// (timeout), while an old Android shell has no `webToNative.getAppData` at all
// and the SDK throws synchronously (catch). Treating only the timeout as
// "unavailable" made Android silently fall through with no telemetry while iOS
// marked the store unavailable — the two must be indistinguishable to callers.
function withTimeout<TResponse>(
    invoke: (callback: (response: TResponse) => void) => void,
    onUnavailable: () => void,
): Promise<TResponse | null> {
    return new Promise((resolve) => {
        let settled = false

        const timer = setTimeout(() => {
            if (settled) return
            settled = true
            onUnavailable()
            resolve(null)
        }, NATIVE_CALL_TIMEOUT_MS)

        try {
            invoke((response) => {
                if (settled) return
                settled = true
                clearTimeout(timer)
                resolve(response)
            })
        } catch (error) {
            if (settled) return
            settled = true
            clearTimeout(timer)
            console.error('nativeSessionStore: native bridge call threw', error)
            onUnavailable()
            resolve(null)
        }
    })
}

const parseStoredValue = (value: unknown): unknown => {
    if (typeof value !== 'string') return value
    try {
        return JSON.parse(value)
    } catch {
        return null
    }
}

const isNativeSessionShape = (value: unknown): value is NativeSessionPayload => {
    if (typeof value !== 'object' || value === null) return false
    const candidate = value as Partial<NativeSessionPayload>
    return (
        candidate.v === 1 &&
        typeof candidate.jwt === 'string' &&
        typeof candidate.userId === 'string' &&
        typeof candidate.origin === 'string'
    )
}

const validateSessionPayload = (rawValue: unknown, expectedOrigin: string): NativeSessionPayload | null => {
    const parsed = parseStoredValue(rawValue)
    if (!isNativeSessionShape(parsed)) {
        // `undefined` is the ordinary "nothing stored yet" answer, not corruption.
        if (rawValue !== undefined && rawValue !== null) noteNativeStoreEvent('invalid')
        return null
    }

    if (parsed.origin !== expectedOrigin) {
        // Cheap to log: origin is an API host, never a secret, and this is the
        // one signal that a stale build is replaying a token from the wrong env.
        console.warn('nativeSessionStore: stored session origin does not match expected origin', {
            storedOrigin: parsed.origin,
            expectedOrigin,
        })
        noteNativeStoreEvent('origin_mismatch')
        return null
    }

    const expiry = decodeJwtExpiry(parsed.jwt)
    if (!expiry) {
        noteNativeStoreEvent('invalid', { detail: 'undecodable_jwt' })
        return null
    }
    if (expiry.getTime() < Date.now() - CLOCK_SKEW_SLACK_MS) {
        noteNativeStoreEvent('invalid', { detail: 'expired' })
        return null
    }

    return parsed
}

/** Fire-and-forget so login never waits on a bridge that may not exist or may hang. */
export const storeNativeSession = (payload: {
    jwt: string
    userId: string
    origin: string
    sessionId?: string
    startedAt?: number
}): void => {
    if (!isAndroidORIosApp) return
    // Once the bridge is known-absent this page load, every further write would
    // just sit in the queue for a full timeout — and logout()'s awaited clear
    // would have to wait behind all of them. Nothing is lost by skipping: the
    // store can't be read back on this shell either.
    if (cachedAvailability === 'unavailable') return
    // Identical session already written this page load — a bridge round trip
    // would change nothing and only risks the shared-registry hazard.
    const writeKey = `${payload.userId}|${payload.jwt}`
    if (writeKey === lastWrittenSession) return
    lastWrittenSession = writeKey

    const sessionPayload: NativeSessionPayload = {
        v: 1,
        jwt: payload.jwt,
        userId: payload.userId,
        origin: payload.origin,
        savedAt: Date.now(),
        ...(payload.sessionId ? { sessionId: payload.sessionId } : {}),
        ...(typeof payload.startedAt === 'number' ? { startedAt: payload.startedAt } : {}),
    }

    enqueue(() =>
        withTimeout<NativeAppDataResponse>(
            (callback) => setAppData({ keyName: STORE_KEY, value: sessionPayload, callback }),
            () => {
                cachedAvailability = 'unavailable'
                noteNativeStoreEvent('write_timeout')
            },
        ),
    ).then((response) => {
        // Any callback at all — success or failure — proves the bridge itself
        // is responsive, which is all this cache is meant to track.
        if (response) {
            cachedAvailability = 'available'
            noteNativeStoreEvent('write_ok')
        }
    })
}

/** Logout awaits this deliberately — a session surviving in native storage post-logout is a silent re-login, not a UX nicety to skip. */
export const clearNativeSession = async (): Promise<{ ok: boolean; timedOut: boolean }> => {
    // The store is empty after a clear, so the next login must write again even
    // if it re-establishes the very same session.
    lastWrittenSession = null
    if (!isAndroidORIosApp) return { ok: true, timedOut: false }
    // Known-absent bridge: there is nothing stored to clear and no point waiting
    // out a timeout on logout — report it as cleared, not as a timeout.
    if (cachedAvailability === 'unavailable') return { ok: true, timedOut: false }

    let timedOut = false
    const response = await enqueue(() =>
        withTimeout<NativeDeleteResponse>(
            (callback) => deleteAppData({ keyName: STORE_KEY, callback }),
            () => {
                timedOut = true
            },
        ),
    )

    // A bridge-responsive-but-isSuccess:false delete is still not an ack — only
    // a genuine isSuccess:true counts as "cleared".
    const ok = response?.isSuccess === true
    noteNativeStoreEvent(ok ? 'clear_ok' : 'clear_timeout', { timedOut })
    return { ok, timedOut }
}

/** Validates before trusting native storage — a stale, wrong-origin, or expired payload replaying login is worse than finding none. */
export const restoreNativeSession = async (expectedOrigin: string): Promise<NativeSessionPayload | null> => {
    if (!isAndroidORIosApp) return null
    // Known-absent bridge this page load (as for store/clear): asking again only waits out another
    // timeout, and a resume-time check stalled on it answers after a boot guest registration landed.
    if (cachedAvailability === 'unavailable') return null

    const response = await enqueue(() =>
        withTimeout<NativeAppDataResponse>(
            (callback) => getAppData({ keyName: STORE_KEY, callback }),
            () => {
                cachedAvailability = 'unavailable'
                // The single most important native-health signal: the bridge did
                // not answer, so a real session may exist that we simply cannot
                // see. Callers treat this as "unconfirmed", never "logged out".
                noteNativeStoreEvent('unavailable')
            },
        ),
    )

    if (!response) return null

    cachedAvailability = 'available'
    return validateSessionPayload(response.value, expectedOrigin)
}
